import React, { useState, useRef } from "react";
import Sidebar from "./Sidebar";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faFileCsv,
  faFileAlt,
  faCheckCircle,
  faSpinner,
  faCloudUploadAlt,
  faDownload,
  faBookOpen,
  faArrowUpFromBracket,
  faUsers,
  faHome,
} from "@fortawesome/free-solid-svg-icons";
import "./addFamilyFromCsv.css";

const AddFamilyFromCSV = () => {
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === "text/csv") {
      validateAndSetFile(droppedFile);
    } else {
      Swal.fire({
        icon: "error",
        title: "Invalid File",
        text: "Please upload a valid CSV file.",
        confirmButtonColor: "#2E2A74",
      });
    }
  };

  const validateAndSetFile = (selectedFile) => {
    if (
      selectedFile.type !== "text/csv" &&
      !selectedFile.name.endsWith(".csv")
    ) {
      Swal.fire({
        icon: "warning",
        title: "Invalid File Format",
        text: "Please select a CSV file.",
        confirmButtonColor: "#2E2A74",
      });
      return;
    }

    // File size validation (5MB limit)
    if (selectedFile.size > 5 * 1024 * 1024) {
      Swal.fire({
        icon: "warning",
        title: "File Too Large",
        text: "File size must be less than 5MB.",
        confirmButtonColor: "#2E2A74",
      });
      return;
    }

    setFile(selectedFile);
  };

  const handleSubmit = async () => {
    if (!file) {
      Swal.fire({
        icon: "warning",
        title: "No File Selected",
        text: "Please select a CSV file before uploading.",
        confirmButtonColor: "#2E2A74",
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setIsUploading(true);

      const xhr = new XMLHttpRequest();
      xhr.open("POST", "https://mhsdev.online:5000/upload-csv", true);

      // Configure headers
      xhr.setRequestHeader("Accept", "application/json");

      // Progress tracking
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentage = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percentage);
        }
      };

      // Handle response
      xhr.onload = () => {
        setIsUploading(false);

        if (xhr.status === 200) {
          Swal.fire({
            icon: "success",
            title: "Upload Successful!",
            text: "Family data has been successfully imported.",
            confirmButtonColor: "#2E8B57",
            timer: 3000,
          });
          setFile(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Upload Failed",
            text:
              xhr.responseText || "Failed to upload file. Please try again.",
            confirmButtonColor: "#2E2A74",
          });
        }
        setUploadProgress(0);
      };

      // Error handling
      xhr.onerror = () => {
        setIsUploading(false);
        Swal.fire({
          icon: "error",
          title: "Network Error",
          text: "Please check your connection and try again.",
          confirmButtonColor: "#2E2A74",
        });
        setUploadProgress(0);
      };

      // Timeout handling
      xhr.timeout = 300000; // 5 minutes
      xhr.ontimeout = () => {
        setIsUploading(false);
        Swal.fire({
          icon: "error",
          title: "Timeout",
          text: "The upload took too long. Please try again.",
          confirmButtonColor: "#2E2A74",
        });
        setUploadProgress(0);
      };

      xhr.send(formData);
    } catch (error) {
      console.error("Upload error:", error);
      setIsUploading(false);
      Swal.fire({
        icon: "error",
        title: "Upload Error",
        text: "An unexpected error occurred.",
        confirmButtonColor: "#2E2A74",
      });
    }
  };

  return (
    <div className="addFamilyCSV-dashboard-wrapper">
      <Sidebar />
      <div className="addFamilyCSV-main-content">
        <section className="addFamilyCSV-content">
          <div className="addFamilyCSV-content-header">
            <h1 className="addFamilyCSV-title">Bulk Family Registration</h1>
            <p className="addFamilyCSV-subtitle">
              Import multiple family records using CSV format
            </p>
          </div>

          {/* Two horizontal cards container */}
          <div className="addFamilyCSV-horizontal-cards-container">
            {/* Instructions Card (Left - 45%) */}
            <div
              className="addFamilyCSV-horizontal-card addFamilyCSV-instructions-card"
              style={{ marginRight: "1rem" }}
            >
              <div className="addFamilyCSV-card-header">
                <div className="addFamilyCSV-header-icon-wrapper">
                  <FontAwesomeIcon
                    icon={faHome}
                    className="addFamilyCSV-card-icon"
                  />
                </div>
                <div>
                  <h3 className="addFamilyCSV-card-title">
                    Family Import Guidelines
                  </h3>
                  <p className="addFamilyCSV-card-subtitle">
                    Ensure successful family data import by following these
                    steps
                  </p>
                </div>
              </div>

              <div className="addFamilyCSV-instructions-content">
                <div className="addFamilyCSV-instruction-step">
                  <div className="addFamilyCSV-step-number">1</div>
                  <div className="addFamilyCSV-step-content">
                    <h4 className="addFamilyCSV-step-title">
                      Review Family Template
                    </h4>
                    <p className="addFamilyCSV-step-description">
                      Download and review the family CSV template to understand
                      required family structure and fields
                    </p>
                  </div>
                </div>

                <div className="addFamilyCSV-instruction-step">
                  <div className="addFamilyCSV-step-number">2</div>
                  <div className="addFamilyCSV-step-content">
                    <h4 className="addFamilyCSV-step-title">
                      Prepare Family Data
                    </h4>
                    <p className="addFamilyCSV-step-description">
                      Ensure all family details, members, and relationships are
                      correctly formatted
                    </p>
                  </div>
                </div>

                <div className="addFamilyCSV-instruction-step">
                  <div className="addFamilyCSV-step-number">3</div>
                  <div className="addFamilyCSV-step-content">
                    <h4 className="addFamilyCSV-step-title">
                      Validate Family Information
                    </h4>
                    <p className="addFamilyCSV-step-description">
                      Check family relationships, contact details, and required
                      fields before upload
                    </p>
                  </div>
                </div>
              </div>

              <div className="addFamilyCSV-card-actions">
                <a
                  href="/family-csv-sample.csv"
                  className="addFamilyCSV-action-btn addFamilyCSV-primary-action"
                  download
                >
                  <FontAwesomeIcon icon={faDownload} />
                  <span>Download Family CSV Template</span>
                </a>
                <a
                  href="/familyCSV-Instructions.docx"
                  className="addFamilyCSV-action-btn addFamilyCSV-secondary-action"
                  download
                >
                  <FontAwesomeIcon icon={faFileAlt} />
                  <span>Download Detailed Instructions</span>
                </a>
              </div>
            </div>

            {/* Upload Card (Right - 45%) */}
            <div className="addFamilyCSV-horizontal-card addFamilyCSV-upload-card">
              <div className="addFamilyCSV-card-header">
                <div className="addFamilyCSV-header-icon-wrapper">
                  <FontAwesomeIcon
                    icon={faArrowUpFromBracket}
                    className="addFamilyCSV-card-icon"
                  />
                </div>
                <div>
                  <h3 className="addFamilyCSV-card-title">
                    Upload Family CSV File
                  </h3>
                  <p className="addFamilyCSV-card-subtitle">
                    Select or drag & drop your prepared family CSV file
                  </p>
                </div>
              </div>

              <div
                className={`addFamilyCSV-upload-area ${
                  isDragOver ? "addFamilyCSV-drag-over" : ""
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="addFamilyCSV-upload-icon-wrapper">
                  <FontAwesomeIcon
                    icon={faCloudUploadAlt}
                    className="addFamilyCSV-upload-area-icon"
                  />
                </div>
                <h4 className="addFamilyCSV-upload-area-title">
                  Drag & Drop Family CSV File
                </h4>
                <p className="addFamilyCSV-upload-subtitle">
                  Supported format: .csv (Maximum 5MB)
                </p>

                <div className="addFamilyCSV-upload-actions">
                  <label
                    htmlFor="file-upload"
                    className="addFamilyCSV-browse-btn"
                  >
                    Browse Family Files
                    <input
                      ref={fileInputRef}
                      id="file-upload"
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      className="addFamilyCSV-upload-file-input"
                    />
                  </label>
                  <p className="addFamilyCSV-upload-note">
                    or drop your family CSV file anywhere in this area
                  </p>
                </div>
              </div>

              {file && (
                <div className="addFamilyCSV-file-preview-section">
                  <div className="addFamilyCSV-file-preview-header">
                    <FontAwesomeIcon
                      icon={faFileCsv}
                      className="addFamilyCSV-file-type-icon"
                    />
                    <span>Selected Family File</span>
                  </div>
                  <div className="addFamilyCSV-file-details">
                    <div className="addFamilyCSV-file-info">
                      <span className="addFamilyCSV-file-name">
                        {file.name}
                      </span>
                      <span className="addFamilyCSV-file-size">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                    <div className="addFamilyCSV-file-status">
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="addFamilyCSV-status-icon"
                      />
                      <span>Ready to upload</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="addFamilyCSV-upload-controls">
                <button
                  className={`addFamilyCSV-upload-btn ${
                    !file || isUploading ? "addFamilyCSV-disabled" : ""
                  }`}
                  onClick={handleSubmit}
                  disabled={isUploading || !file}
                >
                  {isUploading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      <span>Uploading... {uploadProgress}%</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faUpload} />
                      <span>Upload Family Data</span>
                    </>
                  )}
                </button>
              </div>

              {isUploading && (
                <div className="addFamilyCSV-upload-progress-section">
                  <div className="addFamilyCSV-progress-header">
                    <span>Family Data Upload Progress</span>
                    <span className="addFamilyCSV-progress-percentage">
                      {uploadProgress}%
                    </span>
                  </div>
                  <div className="addFamilyCSV-progress-bar-container">
                    <div
                      className="addFamilyCSV-progress-bar-fill"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="addFamilyCSV-progress-note">
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Processing family information. Please wait...
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AddFamilyFromCSV;
